import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { authService } from '@/services/authService';
import { useAuthStore } from '@/store/useAuthStore';
import { MdEmail, MdCheckCircle, MdError, MdRefresh } from 'react-icons/md';
import { useNavigationStore } from '@/common/store';

type VerificationStatus = 'loading' | 'success' | 'error';

const EmailVerificationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {   clearBackNavigation } = useNavigationStore();
  const { user } = useAuthStore();

  const [countdown, setCountdown] = useState(10);
  const [status, setStatus] = useState<VerificationStatus>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const timeRef = useRef<NodeJS.Timeout | null>(null);
  
  const token = searchParams.get('token');

  useEffect(() => {
    clearBackNavigation();
    
  }, []);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('유효하지 않은 인증 링크입니다.');
      return;
    }

    verifyEmail(token);
  }, [token]);

  const verifyEmail = async (verificationToken: string) => {
    try {
      setStatus('loading');
      
      const result = await authService.verifyEmail(verificationToken);
      
      if (result.success) {
        setStatus('success');
        
        // 사용자 정보 업데이트
        if (user) {
          await authService.setUserProfile();
        }
                
        // 10초 후 메인 페이지로 리다이렉트
        setTimeout(() => {
          navigate('/');
        }, 10000);
        
      } else {
        setStatus('error');
        setErrorMessage(result.message || '인증 처리 중 오류가 발생했습니다.');
      }
    } catch (error: any) {
      setStatus('error');
      setErrorMessage(error?.response?.data?.message || '인증 처리 중 오류가 발생했습니다.');
    }
  };


  const handleGoToLogin = () => {
    navigate('/login');
  };

  const handleGoToHome = () => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
      timeRef.current = null;
    }
    navigate('/');
  };

  useEffect(() => {
    if (status === 'success') {
      timeRef.current = setInterval(() => {
        setCountdown((prev) => {
          console.log('countdown', prev);
          if (prev <= 1) {
            handleGoToHome();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      
      return () => {
        if (timeRef.current) {
          clearInterval(timeRef.current);
          timeRef.current = null;
        }
        timeRef.current = null;
      };
    }
  }, [status]);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
        timeRef.current = null;
      }
    };
  }, [timeRef]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <ContentSection>
            <IconContainer $status="loading">
              <LoadingSpinner />
            </IconContainer>
            <Title>이메일 인증 중...</Title>
            <Description>
              인증을 처리하고 있습니다. 잠시만 기다려주세요.
            </Description>
          </ContentSection>
        );

      case 'success':
        return (
          <ContentSection>
            <IconContainer $status="success">
              <MdCheckCircle size={64} />
            </IconContainer>
            <Title>인증 완료!</Title>
            <Description>
              이메일 인증이 성공적으로 완료되었습니다.<br/>
              이제 모든 서비스를 이용하실 수 있습니다.
            </Description>
            <ButtonGroup>
              <PrimaryButton onClick={handleGoToHome}>
                메인으로 이동
              </PrimaryButton>
            </ButtonGroup>
            <AutoRedirectText>
              {countdown}초 후 자동으로 메인 페이지로 이동합니다.
            </AutoRedirectText>
          </ContentSection>
        );

      case 'error':
      default:
        return (
          <ContentSection>
            <IconContainer $status="error">
              <MdError size={64} />
            </IconContainer>
            <Title>인증 실패</Title>
            <Description>
              {errorMessage || '인증 처리 중 오류가 발생했습니다.'}
            </Description>
            <ButtonGroup>
              <SecondaryButton onClick={handleGoToLogin}>
                로그인 페이지로
              </SecondaryButton>
            </ButtonGroup>
          </ContentSection>
        );
    }
  };

  return (
    <Container>
      <ContentWrapper>
        <LogoSection>
          <BrandText>Factsheet</BrandText>
        </LogoSection>
        {renderContent()}
      </ContentWrapper>
    </Container>
  );
};

// 스타일드 컴포넌트들
const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.secondary};
  padding: 40px 20px;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 480px;
  text-align: center;
`;

const LogoSection = styled.div`
  margin-bottom: 64px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const BrandText = styled.h1`
  ${typography.heading.h1}
  color: ${semanticColors.text.brand};
  margin: 0;
  font-weight: 700;
  letter-spacing: -0.02em;
  font-size: 48px;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const ContentSection = styled.div`
  text-align: center;
  padding: 0;
`;

const IconContainer = styled.div<{ $status: VerificationStatus }>`
  margin-bottom: 32px;
  display: flex;
  justify-content: center;
  
  color: ${props => {
    switch (props.$status) {
      case 'success':
        return semanticColors.text.onSuccess.primary;
      case 'error':
        return semanticColors.text.onDanger.secondary;
      case 'loading':
      default:
        return semanticColors.text.brand;
    }
  }};
`;

const LoadingSpinner = styled.div`
  width: 64px;
  height: 64px;
  border: 4px solid ${semanticColors.border.tertiary};
  border-top: 4px solid ${semanticColors.background.brand};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Title = styled.h2`
  ${typography.heading.h3}
  color: ${semanticColors.text.primary};
  margin: 0 0 16px 0;
  font-weight: 600;
`;

const Description = styled.p`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.secondary};
  line-height: 1.6;
  margin: 0 0 48px 0;
  font-size: 16px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 48px;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
  max-width: 280px;
  margin-left: auto;
  margin-right: auto;
`;

const PrimaryButton = styled.button`
  ${typography.paragraph.p2.default}
  padding: 14px 28px;
  background: ${semanticColors.background.brand};
  color: ${semanticColors.text.white};
  border: none;
  border-radius: 6px;
  font-weight: 500;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover:not(:disabled) {
    background: ${semanticColors.background.brandHover};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  ${typography.paragraph.p2.default}
  padding: 14px 28px;
  background: transparent;
  color: ${semanticColors.text.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 6px;
  font-weight: 500;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.primaryHover};
    border-color: ${semanticColors.border.secondary};
  }
`;

const AutoRedirectText = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
  margin: 0;
  font-size: 14px;
`;

export default EmailVerificationPage;
